import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Container, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { getAllShops, convertShopNameToUrl } from '../../services/shopService';
import { Link } from 'react-router-dom';
import SearchBar from '../../components/SearchBar';

// Importo le immagini locali per le categorie
import foodImage from '../../assets/img/food.png';
import abbigliamentoImage from '../../assets/img/abbigliamento.png';
import techImage from '../../assets/img/tech.png';
import casalinghiImage from '../../assets/img/casalinghi.png';

const ShopPage = () => {
  const { t } = useTranslation();
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    const loadShops = async () => {
      try {
        setLoading(true);
        const shopsData = await getAllShops();
        setShops(shopsData);
      } catch (err) {
        console.error('Errore nel caricamento dei negozi:', err);
        setError('Errore nel caricamento dei dati');
      } finally {
        setLoading(false);
      }
    };

    loadShops();
  }, []);

  const filteredShops = shops.filter(shop => {
    // Filtro per categoria del negozio (scelta in ShopSettings)
    if (selectedCategory) {
      if (!shop.category || shop.category.toLowerCase() !== selectedCategory.toLowerCase()) {
        return false;
      }
    }
    // Filtro per testo di ricerca
    if (searchQuery) {
      return (
        shop.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (shop.category && shop.category.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (shop.categories && shop.categories.some(c =>
          c.toLowerCase().includes(searchQuery.toLowerCase())
        ))
      );
    }
    return true;
  });

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status" />
      </div>
    );
  }

  if (error) {
    return <div className="text-center py-5 text-danger">{error}</div>;
  }

  return (
    <div className="fade-in">
      <Container fluid className="px-0">
        {/* Barra di ricerca */}
        <div className="search-container mb-4">
          <SearchBar 
            placeholder={t('shop.search_placeholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Categorie stile Figma */}
        <div className="category-container">
          <div className="category-row">
            {/* Categoria Food */}
            <div
              className={`category-card category-card-pink ${selectedCategory === 'Food' ? 'category-card-active' : ''}`}
              onClick={() => setSelectedCategory(selectedCategory === 'Food' ? null : 'Food')}
              style={{ cursor: 'pointer' }}
            >
              <h3 className="category-title">Food</h3>
              <p className="category-subtitle">Ristoranti</p>
              <img
                src={foodImage}
                alt="Food"
                className="category-image"
              />
            </div>

            {/* Categoria Abbigliamento */}
            <div
              className={`category-card category-card-blue ${selectedCategory === 'Abbigliamento' ? 'category-card-active' : ''}`}
              onClick={() => setSelectedCategory(selectedCategory === 'Abbigliamento' ? null : 'Abbigliamento')}
              style={{ cursor: 'pointer' }}
            >
              <h3 className="category-title">Abbigliamento</h3>
              <p className="category-subtitle">Negozio</p>
              <img
                src={abbigliamentoImage}
                alt="Abbigliamento"
                className="category-image"
              />
            </div>
          </div>
          
          <div className="category-row">
            {/* Categoria Tecnologia */}
            <div
              className={`category-card category-card-gray ${selectedCategory === 'Elettronica' ? 'category-card-active' : ''}`}
              onClick={() => setSelectedCategory(selectedCategory === 'Elettronica' ? null : 'Elettronica')}
              style={{ cursor: 'pointer' }}
            >
              <h3 className="category-title">Elettronica</h3>
              <p className="category-subtitle">Negozio</p>
              <img
                src={techImage}
                alt="Tecnologia"
                className="category-image"
              />
            </div>

            {/* Categoria Casalinghi */}
            <div
              className={`category-card category-card-orange category-card-casalinghi ${selectedCategory === 'Casalinghi' ? 'category-card-active' : ''}`}
              onClick={() => setSelectedCategory(selectedCategory === 'Casalinghi' ? null : 'Casalinghi')}
              style={{ cursor: 'pointer' }}
            >
              <h3 className="category-title">Casalinghi</h3>
              <p className="category-subtitle">Negozio</p>
              <img
                src={casalinghiImage}
                alt="Casalinghi"
                className="category-image"
              />
            </div>
          </div>
        </div>

        {/* Lista dei negozi */}
        <div className="shop-container">
          <h4 className="fw-bold mb-3" style={{ fontSize: '1.4rem' }}>Tutti i negozi</h4>
          
          {filteredShops.map(shop => (
            <Link to={`/${convertShopNameToUrl(shop.name)}`} key={shop.id} className="text-decoration-none">
              <Card className="mb-4 rounded-4 shop-card">
                <div className="shop-rating-badge position-absolute top-0 end-0 m-2">
                  <div className="rating-badge d-flex align-items-center">
                    <i className="bi bi-star-fill text-warning me-1"></i>
                    <span className="fw-bold">{shop.rating}</span>
                  </div>
                </div>
                <Card.Body className="p-3">
                  <Row className="align-items-center">
                    <Col xs={2} className="text-center">
                      <div className="rounded-circle overflow-hidden bg-white mx-auto" style={{ width: '60px', height: '60px' }}>
                        <img 
                          src={shop.logoUrl} 
                          alt={shop.name} 
                          style={{ 
                            width: '100%', 
                            height: '100%', 
                            objectFit: 'cover',
                            objectPosition: 'center'
                          }}
                        />
                      </div>
                    </Col>
                    <Col xs={7} className="ps-3">
                      <Card.Title className="fs-5 mb-0 text-dark">{shop.name}</Card.Title>
                      <Card.Text className="text-muted mb-0">
                        {shop.subtitle || 'Nessuna descrizione'}
                      </Card.Text>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Link>
          ))}

          {filteredShops.length === 0 && (
            <div className="text-center py-5">
              <p className="text-muted">Nessun negozio trovato</p>
            </div>
          )}
        </div>
      </Container>
    </div>
  );
};

export default ShopPage; 